// Data access layer over Supabase. All public site reads + admin writes
// go through these helpers. RLS handles authorization in the database.
import { supabase, requireClient } from "@/lib/supabase";
import { FALLBACK_SERVICES, FALLBACK_TESTIMONIALS } from "@/lib/fallbackContent";

// ---------- Resilient public reads ----------
const READ_TIMEOUT_MS = 8000;
const CACHE_PREFIX = "tdt-public-";
const FALLBACKS = { services: FALLBACK_SERVICES, testimonials: FALLBACK_TESTIMONIALS, settings: null };

const QUERIES = {
  services: (c) => c.from("services").select("*").eq("is_active", true).order("sort_order", { ascending: true }),
  testimonials: (c) => c.from("testimonials").select("*").eq("is_published", true).order("sort_order", { ascending: true }),
  settings: (c) => c.from("site_settings").select("*").eq("id", "settings").maybeSingle(),
};

export function readPublicCache(key) {
  try {
    const raw = window.localStorage.getItem(CACHE_PREFIX + key);
    if (raw) return JSON.parse(raw);
  } catch (_) { /* storage unavailable */ }
  return FALLBACKS[key];
}

function writePublicCache(key, data) {
  try { window.localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(data)); } catch (_) { /* ignore */ }
}

/** Fresh read with timeout + one retry. Throws on failure (used by admin verification). */
export async function fetchPublicStrict(key) {
  const client = requireClient();
  let lastErr;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const { data, error } = await QUERIES[key](client).abortSignal(AbortSignal.timeout(READ_TIMEOUT_MS));
      if (error) throw error;
      const result = key === "settings" ? data : (data ?? []);
      writePublicCache(key, result);
      return result;
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr;
}

/** Guest-facing read: never throws — falls back to last good cache or built-in content. */
async function fetchPublicSafe(key) {
  try {
    return await fetchPublicStrict(key);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[data] public ${key} read failed, using cached content`, err);
    return readPublicCache(key);
  }
}

export const getPublicSiteSettings = () => fetchPublicSafe("settings");
export const listPublicServices = () => fetchPublicSafe("services");
export const listPublicTestimonials = () => fetchPublicSafe("testimonials");

// ---------- Site Settings (singleton id='settings') ----------
export async function getSiteSettings() {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from("site_settings")
    .select("*")
    .eq("id", "settings")
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function updateSiteSettings(updates) {
  const client = requireClient();
  // upsert keeps it safe if the row was ever deleted
  const { data, error } = await client
    .from("site_settings")
    .upsert({ id: "settings", ...updates }, { onConflict: "id" })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ---------- Services ----------
export async function listAllServices() {
  const client = requireClient();
  const { data, error } = await client
    .from("services")
    .select("*")
    .order("sort_order", { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function createService(payload) {
  const client = requireClient();
  const { data, error } = await client.from("services").insert(payload).select().single();
  if (error) throw error;
  return data;
}

export async function updateService(id, updates) {
  const client = requireClient();
  const { data, error } = await client.from("services").update(updates).eq("id", id).select().single();
  if (error) throw error;
  return data;
}

export async function deleteService(id) {
  const client = requireClient();
  const { error } = await client.from("services").delete().eq("id", id);
  if (error) throw error;
}

// ---------- Testimonials ----------
export async function listAllTestimonials() {
  const client = requireClient();
  const { data, error } = await client
    .from("testimonials")
    .select("*")
    .order("sort_order", { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function createTestimonial(payload) {
  const client = requireClient();
  const { data, error } = await client.from("testimonials").insert(payload).select().single();
  if (error) throw error;
  return data;
}

export async function updateTestimonial(id, updates) {
  const client = requireClient();
  const { data, error } = await client.from("testimonials").update(updates).eq("id", id).select().single();
  if (error) throw error;
  return data;
}

export async function deleteTestimonial(id) {
  const client = requireClient();
  const { error } = await client.from("testimonials").delete().eq("id", id);
  if (error) throw error;
}

// ---------- Waitlist ----------
// No .select() — anonymous visitors may INSERT but not read the waitlist (RLS).
export async function submitWaitlist(payload) {
  const client = requireClient();
  const { error } = await client.from("waitlist_submissions").insert(payload);
  if (error) throw error;
}

export async function listWaitlist() {
  const client = requireClient();
  const { data, error } = await client
    .from("waitlist_submissions")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function updateWaitlistStatus(id, status) {
  const client = requireClient();
  const { data, error } = await client
    .from("waitlist_submissions")
    .update({ status })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteWaitlist(id) {
  const client = requireClient();
  const { error } = await client.from("waitlist_submissions").delete().eq("id", id);
  if (error) throw error;
}
